<template>
  <div
    :class="{
      busy: isBusy || loading,
      'document-metadata-calendar-input': true,
    }"
  >
    <PVDatePicker
      :default-value="currentValue"
      :disabled="disabled || isBusy || loading"
      :manual-input="false"
      fluid
      :invalid="!isInputValid"
      @value-change="handleDateUpdated"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { isSingleDateEvent } from '@/utils';
  import { DatePickerValueChangeEvent } from '@/interfaces';
  import { UpdateMetadataFunction } from '../DocumentsMetadata/DocumentsMetadata.types';

  interface DocumentMetadataCalendarInputProps {
    disabled?: boolean;
    initialValue: string;
    getMetadataFunction?: () => Promise<unknown> | unknown;
    metadataField: string;
    validationFunction: (value?: Date) => boolean;
    updateMetadataFunction: UpdateMetadataFunction;
    isWaitingForValues?: boolean;
    loading?: boolean;
  }

  const props = withDefaults(
    defineProps<DocumentMetadataCalendarInputProps>(),
    { disabled: false },
  );

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { t } = useI18n();
  const { createToast } = useToast();

  const currentValue = ref<Date | undefined>();
  const isBusy = ref(false);
  const isInputValid = ref(true);

  function validateInput(): void {
    isInputValid.value = props.validationFunction(currentValue.value);
  }

  async function handleDateUpdated(
    date: DatePickerValueChangeEvent,
  ): Promise<void> {
    if (!isSingleDateEvent(date)) return;
    isBusy.value = true;
    const transformedDate = date.toLocaleDateString('en-US');

    try {
      await props.updateMetadataFunction(props.metadataField, transformedDate);
      await props.getMetadataFunction?.();
    } catch {
      createToast(t('errors.error'), t('errors.error'), 'danger');
    } finally {
      validateInput();
      isBusy.value = false;
      emit('document-data-changed');
    }
  }

  watch(
    () => props.initialValue,
    (initialValue) => {
      currentValue.value = initialValue ? new Date(initialValue) : undefined;
    },
    { immediate: true },
  );

  watch(
    () => [currentValue.value, props.validationFunction],
    () => {
      validateInput();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metadata-calendar-input {
    #date-range-picker-container {
      min-width: 100%;
      height: 40px;
    }

    &.busy {
      @include pulsating-animation;
    }
  }
</style>
